import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import WebHeader from "./WebHeader";
import WebFooter from "./WebFooter";
import axios from "axios";

const API_BASE = "http://144.24.143.236:5000/api";

// ── Step indicators ──────────────────────────────────────────────────────────
const steps = ["Upload PAN", "Upload Resume", "Done"];

const StepBar = ({ current }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: 28 }}>
    {steps.map((s, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <React.Fragment key={s}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: done ? "#16a34a" : active ? "#2563eb" : "#e5e7eb",
              color: done || active ? "#fff" : "#9ca3af",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 700, fontSize: 14, transition: "all .3s"
            }}>
              {done ? "✓" : i + 1}
            </div>
            <span style={{ fontSize: 11, color: active ? "#2563eb" : done ? "#16a34a" : "#9ca3af", fontWeight: active ? 700 : 500 }}>
              {s}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{
              flex: 1, height: 2, margin: "0 6px 18px",
              background: done ? "#16a34a" : "#e5e7eb", transition: "background .3s"
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── File Drop Zone ───────────────────────────────────────────────────────────
const DropZone = ({ accept, label, icon, file, onChange }) => {
  const inputRef = useRef();
  const [drag, setDrag] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files[0];
    if (f) onChange({ target: { files: [f] } });
  };

  return (
    <div
      onClick={() => inputRef.current.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${drag ? "#2563eb" : file ? "#16a34a" : "#d1d5db"}`,
        borderRadius: 12, padding: "28px 20px", textAlign: "center",
        cursor: "pointer", background: drag ? "#eff6ff" : file ? "#f0fdf4" : "#f9fafb",
        transition: "all .2s"
      }}
    >
      <input ref={inputRef} type="file" accept={accept} style={{ display: "none" }}
        onChange={onChange} />
      <div style={{ fontSize: 32, marginBottom: 8 }}>{file ? "✅" : icon}</div>
      <div style={{ fontWeight: 600, color: file ? "#16a34a" : "#374151", fontSize: 14 }}>
        {file ? file.name : label}
      </div>
      {!file && (
        <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
          Click or drag & drop
        </div>
      )}
    </div>
  );
};

// ── Modal ────────────────────────────────────────────────────────────────────
const Modal = ({ open, onClose, children }) => {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.45)", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 16
    }} onClick={onClose}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: 32,
        width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,.2)",
        position: "relative", animation: "modalIn .25s ease"
      }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{
          position: "absolute", top: 14, right: 16, background: "none",
          border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280"
        }}>✕</button>
        {children}
      </div>
      <style>{`@keyframes modalIn { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }`}</style>
    </div>
  );
};

// ── Alert ────────────────────────────────────────────────────────────────────
const Alert = ({ type, msg }) => {
  if (!msg) return null;
  const colors = { error: ["#fef2f2", "#dc2626"], success: ["#f0fdf4", "#16a34a"], info: ["#eff6ff", "#2563eb"] };
  const [bg, color] = colors[type] || colors.info;
  return (
    <div style={{ background: bg, color, borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: 16, fontWeight: 500 }}>
      {msg}
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const JobDetails = () => {
  const { id: job_id } = useParams();
  const [job, setJob] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Step state
  const [step, setStep] = useState(0); // 0=PAN, 1=Resume, 2=Done

  // PAN step
  const [panFile, setPanFile] = useState(null);
  const [panLoading, setPanLoading] = useState(false);
  const [panError, setPanError] = useState("");
  const [panData, setPanData] = useState(null); // holds candidate_id etc.

  // Resume step
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState("");
  const [resumeResult, setResumeResult] = useState(null);

  useEffect(() => { if (job_id) loadJob(); }, [job_id]);

  const loadJob = async () => {
    try {
      const res = await axios.get(`${API_BASE}/jobs/${job_id}`);
      if (res.data.status) setJob(res.data.data);
    } catch (err) { console.error(err); }
  };

  const openModal = () => {
    setStep(0); setPanFile(null); setPanData(null); setPanError("");
    setResumeFile(null); setResumeResult(null); setResumeError("");
    setModalOpen(true);
  };

  // ── PAN Upload ──
  const handlePanSubmit = async () => {
    if (!panFile) { setPanError("Please select a PAN card image."); return; }
    setPanLoading(true); setPanError("");
    try {
      const form = new FormData();
      form.append("pan_image", panFile);
      form.append("job_id", job_id);
      const res = await axios.post(`${API_BASE}/apply/upload-pan`, form);
      if (res.data.status) {
        setPanData(res.data.data);
        setStep(1);
      } else {
        setPanError(res.data.message || "PAN upload failed.");
      }
    } catch (err) {
      setPanError(err.response?.data?.detail || err.response?.data?.message || "PAN upload failed.");
    } finally { setPanLoading(false); }
  };

  // ── Resume Upload ──
  const handleResumeSubmit = async () => {
    if (!resumeFile) { setResumeError("Please select your resume file."); return; }
    setResumeLoading(true); setResumeError("");
    try {
      const form = new FormData();
      form.append("resume", resumeFile);
      form.append("job_id", job_id);
      form.append("candidate_id", panData.candidate_id);
      const res = await axios.post(`${API_BASE}/apply/upload-resume`, form);
      if (res.data.status) {
        setResumeResult(res.data.data);
        setStep(2);
      } else {
        setResumeError(res.data.message || "Resume upload failed.");
      }
    } catch (err) {
      setResumeError(err.response?.data?.detail || err.response?.data?.message || "Resume upload failed.");
    } finally { setResumeLoading(false); }
  };

  if (!job) return <p style={{ padding: 40, textAlign: "center" }}>Loading job details...</p>;

  return (
    <>
      <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

    * { font-family: 'DM Sans', sans-serif; }
    h1,h2,h3,h4,h5,h6 { font-family: 'Syne', sans-serif; }
  `}</style>
      <WebHeader />
      <main className="main-wrappers pt-4 pb-5">
        <div className="main-content container">
          <div className="row">

            {/* ── Job Details ── */}
            <div className="col-md-8">
              <div className="rounded p-4 bg-white" style={{ boxShadow: "0 2px 12px rgba(0,0,0,.07)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <h3 style={{ marginBottom: 4 }}>{job.title}</h3>
                    <span style={{
                      background: "#eff6ff", color: "#2563eb",
                      borderRadius: 20, padding: "3px 12px", fontSize: 13, fontWeight: 600
                    }}>
                      {job.job_type || "Full Time"}
                    </span>
                  </div>
                  <button onClick={openModal} style={{
                    background: "#2563eb", color: "#fff", border: "none",
                    borderRadius: 8, padding: "10px 24px", fontWeight: 700,
                    fontSize: 15, cursor: "pointer", transition: "background .2s"
                  }}
                    onMouseOver={e => e.target.style.background = "#1d4ed8"}
                    onMouseOut={e => e.target.style.background = "#2563eb"}
                  >
                    Apply Now
                  </button>
                </div>

                <hr style={{ margin: "16px 0" }} />

                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 16 }}>
                  {[
                    ["🏢", "Company", job.company],
                    ["📍", "Location", job.location],
                    ["💰", "Salary", job.salary],
                  ].map(([icon, label, val]) => val && (
                    <div key={label}>
                      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>{icon} {label}</div>
                      <div style={{ fontWeight: 600, color: "#111827" }}>{val}</div>
                    </div>
                  ))}
                </div>

                {job.description && (
                  <>
                    <h6 style={{ fontWeight: 700, marginBottom: 8 }}>Job Description</h6>
                    <p style={{ color: "#374151", lineHeight: 1.7 }}>{job.description}</p>
                  </>
                )}
              </div>
            </div>

            {/* ── Premium Jobs ── */}
            <div className="col-md-4">
              <div className="rounded p-3 bg-white" style={{ boxShadow: "0 2px 12px rgba(0,0,0,.07)" }}>
                <h5 style={{ marginBottom: 16 }}>Premium Jobs</h5>
                <div className="menu-card" style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <img style={{ height: 50, width: 50, borderRadius: 8, objectFit: "cover" }}
                    src="/assets/images/auth/cclogo.jpg" alt="logo" />
                  <div>
                    <h6 style={{ margin: 0, fontSize: 14 }}>Premium Job 1</h6>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>Top company hiring now</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* ── Apply Modal ── */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <h4 style={{ marginBottom: 4, fontWeight: 700 }}>Apply for {job.title}</h4>
        <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 20 }}>Complete the steps below to submit your application.</p>

        <StepBar current={step} />

        {/* STEP 0: PAN Upload */}
        {step === 0 && (
          <>
            <Alert type="error" msg={panError} />
            <p style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
              Upload your PAN card to verify your identity and create your candidate profile.
            </p>
            <DropZone
              accept="image/*"
              label="Upload PAN Card Image"
              icon="🪪"
              file={panFile}
              onChange={e => { setPanFile(e.target.files[0]); setPanError(""); }}
            />
            <button
              onClick={handlePanSubmit}
              disabled={panLoading}
              style={{
                marginTop: 20, width: "100%", background: panLoading ? "#93c5fd" : "#2563eb",
                color: "#fff", border: "none", borderRadius: 8, padding: "12px",
                fontWeight: 700, fontSize: 15, cursor: panLoading ? "not-allowed" : "pointer"
              }}
            >
              {panLoading ? "Verifying PAN..." : "Verify & Continue →"}
            </button>
          </>
        )}

        {/* STEP 1: Resume Upload */}
        {step === 1 && (
          <>
            <Alert type="success" msg={`✅ PAN verified! Welcome, ${panData?.full_name}`} />
            <Alert type="error" msg={resumeError} />

            <div style={{
              background: "#f8fafc", borderRadius: 8, padding: "10px 14px",
              fontSize: 12, color: "#475569", marginBottom: 14
            }}>
              <strong>Candidate ID:</strong> {panData?.candidate_id} &nbsp;|&nbsp;
              <strong>PAN:</strong> {panData?.pan_number}
            </div>

            <p style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
              Upload your resume (PDF or DOC). It will be parsed automatically.
            </p>
            <DropZone
              accept=".pdf,.doc,.docx"
              label="Upload Resume (PDF / DOC)"
              icon="📄"
              file={resumeFile}
              onChange={e => { setResumeFile(e.target.files[0]); setResumeError(""); }}
            />
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => setStep(0)} style={{
                flex: 1, background: "#f3f4f6", color: "#374151",
                border: "none", borderRadius: 8, padding: 12, fontWeight: 600, cursor: "pointer"
              }}>← Back</button>
              <button
                onClick={handleResumeSubmit}
                disabled={resumeLoading}
                style={{
                  flex: 2, background: resumeLoading ? "#93c5fd" : "#2563eb",
                  color: "#fff", border: "none", borderRadius: 8, padding: 12,
                  fontWeight: 700, fontSize: 15, cursor: resumeLoading ? "not-allowed" : "pointer"
                }}
              >
                {resumeLoading ? "Submitting Application..." : "Submit Application →"}
              </button>
            </div>
          </>
        )}

        {/* STEP 2: Success */}
        {step === 2 && (
          <div style={{ textAlign: "center", padding: "10px 0" }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
            <h4 style={{ color: "#16a34a", fontWeight: 700 }}>Application Submitted!</h4>
            <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 20 }}>
              Your application has been received. We'll be in touch soon.
            </p>
            <div style={{
              background: "#f0fdf4", borderRadius: 10, padding: "12px 16px",
              textAlign: "left", fontSize: 13, color: "#166534", marginBottom: 20
            }}>
              <div><strong>Application ID:</strong> {resumeResult?.application_id}</div>
              <div><strong>Candidate ID:</strong> {resumeResult?.candidate_id}</div>
              <div><strong>Job ID:</strong> {resumeResult?.job_id}</div>
              <div><strong>Name:</strong> {resumeResult?.parsed?.full_name}</div>
              <div><strong>Email:</strong> {resumeResult?.parsed?.email}</div>
            </div>
            <button onClick={() => setModalOpen(false)} style={{
              background: "#2563eb", color: "#fff", border: "none",
              borderRadius: 8, padding: "10px 28px", fontWeight: 700,
              fontSize: 14, cursor: "pointer"
            }}>Close</button>
          </div>
        )}
      </Modal>

      <WebFooter />
    </>
  );
};

export default JobDetails;
